<template>
  <article class="sicknote models">
    <h4 class="models__title">Modelos de Atestado</h4>

    <p class="models__description">
      Digite abaixo modelos de atestados para enviar facilmente
      para seus pacientes em suas consultas.
    </p>

    <form @submit.prevent="submit()" class="sicknote__form">
      <div
        class="sicknote__form-wrapper"
        :class="[errorClass($v.formData.name), successClass($v.formData.name), (getApiErrors.name && getApiErrors.name.length) && 'has-error']"
      >
        <label class="sicknote__form-label">Atestado</label>

        <input
          class="form-input sicknote__form-field"
          type="text"
          v-model="formData.name"
          placeholder="Nome do atestado"
          @blur="$v.formData.name.$touch()"
        >

        <ul class="sicknote__form-errors">
          <li v-if="!$v.formData.name.required && $v.formData.name.$dirty">
            <span class="form-input-hint">
              campo em branco
            </span>
          </li>

          <li v-if="!$v.formData.name.maxLength && $v.formData.name.$dirty">
            <span class="form-input-hint">
              Você excedeu o limite máximo de
              {{$v.formData.name.$params.maxLength.max}} caracteres.
            </span>
          </li>

          <li v-for="(error, $index) in getApiErrors.name" :key="$index">
            <span class="form-input-hint">{{error}}</span>
          </li>
        </ul>
      </div>

      <div class="sicknote__form-wrapper">
        <label class="sicknote__form-label">CID</label>

        <dr-autocomplete
          @change="setCid"
          :ref="'autocomplete'"
          :format="'{$id} - {$name}'"
          :fields="['name', 'id']"
          :model="cid"
          :store-type="'cid'"
          endpoint="cid"
          placeholder="Selecione o CID"
          reset="true"
          class="sicknote__form-field"
        />
      </div>

      <div
        class="sicknote__form-wrapper"
        :class="[errorClass($v.formData.days), successClass($v.formData.days)]"
      >
        <label class="sicknote__form-label">Dias de afastamento</label>

        <input
          v-model="getDays"
          v-mask="'###'"
          @input="setDays($event.target.value)"
          :disabled="formData.days === 0"
          class="form-input sicknote__form-field"
          placeholder="Dias de afastamento"
          type="text"
        >

        <ul class="sicknote__form-errors">
          <li v-if="!$v.formData.days.required && $v.formData.name.$dirty">
            <span class="form-input-hint">
              campo em branco
            </span>
          </li>

          <li v-for="(error, $index) in getApiErrors.days" :key="$index">
            <span class="form-input-hint">{{error}}</span>
          </li>
        </ul>

        <div class="form-group">
          <label class="form-checkbox">
            <input
              @change="$event.target.checked ? setReturnDays(0) : setReturnDays(1)"
              :checked="this.getDays === 0"
              type="checkbox"
            >
            <i class="form-icon" /> Retorno imediato
          </label>
        </div>
      </div>

      <div
        class="sicknote__form-wrapper"
        :class="[errorClass($v.formData.description), successClass($v.formData.description)]"
      >
        <label class="sicknote__form-label">Atestado</label>

        <ckeditor
          v-model.lazy="getDescription"
          :editor="editor"
          :config="editorConfig"
          @ready="onReady"
          @blur="$v.formData.description.$touch()"
        />

        <ul class="sicknote__form-errors">
          <li v-if="!$v.formData.description.required && $v.formData.description.$dirty">
            <span class="form-input-hint">
              campo em branco
            </span>
          </li>

          <li
            v-for="(error, $index) in getApiErrors.description"
            :key="$index"
          >
            <span class="form-input-hint">
              {{error}}
            </span>
          </li>
        </ul>
      </div>

      <div class="sicknote__form-submit">
        <dr-submit
          :disabled="$v.$invalid"
          :sending="sending"
          class="btn-sm"
          text="Salvar"
        />
      </div>
    </form>

    <section class="models__models" v-if="models.length > 0">
      <header class="models__models-header">
        <h5 class="models__models-title">Modelos</h5>
      </header>

      <hr>

      <ul class="models__models-list">
        <li
          class="models__models-item"
          v-for="model in models"
          :key="model.id"
          :class="formData.id === model.id ? 'active' : ''"
        >
          <a class="models__models-remove" @click="deleteModel(model.id)">
            &times;
          </a>

          <img
            @click="getModel(model)"
            class="clickable models__models-icon"
            src="@/assets/img/icons/ico_png/ico_doc.png"
          />

          <span class="models__models-name">{{model.name}}</span>
        </li>
      </ul>
    </section>
  </article>
</template>

<script>
import drAutocomplete from '@/components/form/autocomplete'
import drSubmit from '@/components/form/submit'
import '@ckeditor/ckeditor5-build-decoupled-document/build/translations/pt-br.js'
import CKEditor from '@ckeditor/ckeditor5-vue'
import DocumentEditor from '@ckeditor/ckeditor5-build-decoupled-document/build/ckeditor.js'
import { required, maxLength } from 'vuelidate/lib/validators'
import cross from '@/assets/img/icons/ico_png/ico_cross.png'
import doc from '@/assets/img/icons/ico_png/ico_doc.png'

export default {
  components: {
    ckeditor: CKEditor.component,
    drAutocomplete,
    drSubmit
  },
  data () {
    return {
      editor: DocumentEditor,
      cid: null,
      models: [],
      cross: cross,
      doc: doc,
      formData: {
        id: null,
        name: null,
        days: 1,
        description: ''
      },
      defaultText: `
      Atesto para os devidos fins de direito que o paciente <mark class="pen-blue">#NomeDoPaciente</mark>,
      portador do documento de identidade (RG) <mark class="pen-blue">#NumeroDoRg</mark>, foi submetido a consulta médica nesta data, sendo portador de afecção <mark class="pen-blue"><strong>#CID-X</strong></mark>.<br /><br />
      Em decorrência, deverá permanecer afastado por um período de <mark class="pen-blue">1<strong>.</strong>dia</mark>, a partir desta data.`,
      immediateReturnText: `Atesto para os devidos fins de direito que o paciente <mark class="pen-blue">#NomeDoPaciente</mark>, portador do documento de identidade (RG) <mark class="pen-blue">#NumeroDoRg</mark>,
        foi submetido à consulta médica nesta data, entre _______________ e _______________, podendo retornar normalmente para suas atividades.`,
      editorConfig: {
        language: 'pt-br',
        removePlugins: [ 'Heading', 'Link' ],
        toolbar: ['bold', 'italic', 'strikethrough', 'bulletedList', 'numberedList', 'blockQuote', 'undo', 'redo'],
        highlight: {
          options: [
            {
              model: 'bluePen',
              class: 'pen-blue',
              title: 'Blue pen',
              color: 'rgb(79, 159, 207)',
              type: 'pen'
            }
          ]
        }
      },
      sending: false,
      getApiErrors: []
    }
  },
  validations () {
    return {
      formData: {
        name: {required, maxLength: maxLength(45)},
        description: {required},
        days: {required}
      }
    }
  },
  computed: {
    getDays: {
      get () {
        return this.formData.days
      },
      set (v) {
        if (!v) {
          this.formData.days = 1
          return
        }
        v = this.fn_NUMBERS_ONLY(v)
        this.formData.days = v
      }
    },
    getDescription: {
      get () {
        return this.formData.description
      },
      set (v) {
        this.formData.description = v
      }
    }
  },
  created () {
    this.formData.description = this.defaultText
    this.updateListModels()
  },
  mounted () {
    this.fn_HIDE_PROGRESS()
  },
  methods: {
    setReturnDays (days) {
      this.formData.days = days
      this.formData.description = days ? this.defaultText : this.immediateReturnText
    },
    getModel (model) {
      this.formData.id = model.id
      this.formData.name = model.name
      this.formData.description = model.description
      const days = model.description.match(/([0-9]+)<strong>.<\/strong>/g) ? model.description.split(/([0-9]+)<strong>.<\/strong>/g)[1] : 0
      this.formData.days = parseInt(this.fn_NUMBERS_ONLY(days))
    },
    updateListModels () {
      this.fn_SHOW_PROGRESS()

      this.$http
        .get('/doctor/sick_note_model?itensPerPage=all', this.$store.getters.getAuthHeader)
        .then((res) => {
          this.models = res.data.data
          this.fn_HIDE_PROGRESS()
        })
        .catch((err) => {
          this.fn_HIDE_PROGRESS()
          console.log('err', err)
          this.fn_FLATTEN_OBJECT(err.errors.children).then(
            (res) => {
              for (let field in res) {
                this.getApiErrors[field.replace(/.children|.errors/g, '')] = res[field]
              }
            }
          )
        })
        .finally(() => {
          this.loaded = true
        })
    },
    errorClass (item) {
      if ((item.$invalid && item.$dirty) || this.getApiErrors.length) {
        return 'has-error'
      }
    },
    successClass (item) {
      if (!item.$invalid && !this.getApiErrors.length) {
        return 'has-success'
      }
    },
    onReady (editor) {
      editor.ui.view.editable.element.parentElement.insertBefore(
        editor.ui.view.toolbar.element,
        editor.ui.view.editable.element
      )
    },
    edit (data) {
      this.$v.formData.description.$touch()
    // data = data.replace(/#cid/g, '<mark class="pen-blue">#CID-X</mark> ')
    // data = data.replace(/#nomedopaciente/g, '<mark class="pen-blue">#NomeDoPaciente</mark> ')
    },

    submit () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }
      let data = {
        name: this.formData.name,
        description: this.getDescription
      }
      this.sending = true
      if (this.formData.id) {
        this.$http.put(`/doctor/sick_note_model/${this.formData.id}`, {'sick_note_model': data}, this.$store.getters.getAuthHeader)
          .then(res => {
            let status = res.code || res.status
            if (status) {
              this.$store.dispatch('setModalAlert', {
                title: 'Sucesso',
                body: 'Dados atualizados',
                size: 'md',
                active: true
              })
            }
            this.formData.id = null
            this.formData.name = null
            this.formData.days = 1
            this.formData.description = this.defaultText
            this.sending = false
          })
          .catch(err => {
            console.log(err)
            this.fn_FLATTEN_OBJECT(err.errors.children).then(
              (res) => {
                for (let field in res) {
                  this.getApiErrors[field.replace(/.children|.errors/g, '')] = res[field]
                }
              }
            )
            this.sending = false
          })
          .finally(() => {
            this.$v.$reset()

            this.updateListModels()
          })

        return
      }

      this.$http.post('/doctor/sick_note_model ', {'sick_note_model': data}, this.$store.getters.getAuthHeader)
        .then(res => {
          let status = res.code || res.status
          if (status) {
            this.$store.dispatch('setModalAlert', {
              title: 'Sucesso',
              body: 'Dados atualizados',
              size: 'md',
              active: true
            })
          }
          this.formData.id = null
          this.formData.name = null
          this.formData.description = this.defaultText
          this.sending = false
        })
        .catch(err => {
          this.sending = false
          this.fn_FLATTEN_OBJECT(err.errors.children).then(
            (res) => {
              for (let field in res) {
                this.getApiErrors[field.replace(/.children|.errors/g, '')] = res[field]
              }
            }
          )
          this.$store.dispatch('setModalAlert', {
            title: 'Erro',
            body: 'Ocorreu um erro, tente novamente mais tarde',
            size: 'md',
            active: true
          })
        })
        .finally(() => {
          this.$v.$reset()

          this.updateListModels()
        })
    },
    deleteModel (id) {
      const self = this
      this.$store.dispatch('setModalConfirm', {
        title: 'Confirmação',
        body: `Tem certeza que deseja remover o modelo?`,
        confirmText: 'Confirmar',
        cancelText: 'Cancelar',
        beforeClose (v) {
          if (!v) {
            this.$store.dispatch('setModalConfirm')
          } else {
            this.fn_SHOW_PROGRESS()
            this.$http.delete(`/doctor/sick_note_model/${id}`, this.$store.getters.getAuthHeader)
              .then(res => {
                let status = res.code || res.status
                if (status) {
                  this.$store.dispatch('setModalAlert', {
                    title: 'Sucesso',
                    body: 'Removido com sucesso',
                    size: 'md',
                    active: true
                  })
                }
              })
              .catch(err => {
                console.log(err)
                this.$store.dispatch('setModalAlert', {
                  title: 'Erro',
                  body: 'Ocorreu um erro, tente novamente mais tarde',
                  size: 'md',
                  active: true
                })
              })
              .finally(() => {
                self.fn_HIDE_PROGRESS()
                self.$store.dispatch('setModalConfirm')
                self.$v.$reset()
                self.formData.id = null
                self.formData.name = null
                self.formData.description = self.defaultText
                self.updateListModels()
              })
          }
        },
        size: 'md',
        active: true
      })
    },
    setCid ($value) {
      console.log('$value', $value)
      if ($value) {
        this.formData.description = this.formData.description.replace(/<strong>#CID-X<\/strong>/g,
          `CID-${$value}; <strong>#CID-X</strong>`)
      }
    },
    setDays () {
      this.formData.description = this.formData.description.replace(/\d+<strong>.<\/strong>dia(s\b|\b)/g,
        `${parseInt(this.fn_NUMBERS_ONLY(this.getDays))}<strong>.</strong>dia${this.getDays > 1 ? 's' : ''}`)
    }
  }
}
</script>
